import { clone, recordActions } from 'mobx-state-tree';
import Report from '../checks/Report';
import { NameCheck } from '../checks/specific';
import { isEmpty } from 'lodash';
import type { IModel, AnyCheck } from '../commands/types';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IMultiplePropagationConstraint } from '../../stores/model/metamodel/multiple_propagation_constraint';

/**
 * The `ModelVersion` store (see `stores/projects/model_version.js`) as the
 * controller layer reads it. That store is still plain JS, so we describe only
 * the surface the controllers touch: the loaded `content` (the MERODE model
 * root) and the parent `model` information that carries the propagation
 * preference. `content` is `null` until `loadContent` runs; controllers are
 * only constructed once a version is loaded.
 */
export interface IModelVersion {
  readonly content: IModel | null;
  readonly model: { readonly propagateOnDependency: boolean };
}

/**
 * Result of a realtime name-input check, surfaced to the UI.
 */
export interface NameInputResult {
  ok: boolean;
  message: string;
}

/**
 * The command callback `executeAndCheck` runs against a cloned model. It mutates
 * the clone and returns the checks the report should aggregate (optionally
 * async, since the FSM-add commands await GUI work).
 */
export type Command = (model: IModel) => AnyCheck[] | Promise<AnyCheck[]>;

/**
 * The runtime contract of a runnable check. Every concrete `Check` subclass
 * (the model-commands layer pushes only concrete subclasses into the result
 * bags) defines `check(): Report`, but the shared base class `Check<T>` —
 * effectively abstract for `check` — does not declare it, so `AnyCheck`
 * (`Check<CheckSubject>`) lacks it in its type. This interface captures the
 * contract the commands layer guarantees so `executeAndCheck` can run the
 * checks without an `any`-dump. Narrow, runtime-neutral, single deref site.
 */
interface RunnableCheck {
  readonly obsolete: boolean;
  check(): Report;
}

export default class Controller {
  modelVersion: IModelVersion;

  constructor(modelVersion: IModelVersion) {
    this.modelVersion = modelVersion;
  }

  // Provide the UI with a method to perform realtime checking of names.
  checkNameInput(name: string): NameInputResult {
    if (!isEmpty(name)) {
      const report = NameCheck.checkInput(name);
      return {
        ok: report.successfulWithoutWarning,
        message: report.firstMessage,
      };
    } else {
      return { ok: true, message: '' };
    }
  }

  // Expose the list of available data types to the UI
  get availableDataTypes(): string[] {
    return this.modelVersion.content!.metamodel.availableDataTypes;
  }

  // Expose the list of object types to the UI
  get availableObjectTypes(): IObjectType[] {
    return this.modelVersion.content!.metamodel.objectTypes;
  }

  // Expose the list of MPCs to the UI
  get availableMultiplePropagationConstraints(): IMultiplePropagationConstraint[] {
    return this.modelVersion.content!.metamodel.multiplePropagationConstraints;
  }

  /**
   * Abstract method to execute a command using the procedure documented in
   * '../../README.md' to ensure model consistency. Subclasses use this to
   * execute their commands.
   *
   * @param {(model) => checks} command callback to do the actual work. Receives
   * a model to execute the command on. Should return a list of checks to
   * perform.
   */
  async executeAndCheck(command: Command): Promise<Report> {
    const report = await new Promise<Report>((resolve, reject) => {
      // Use setTimeout to move this to the next tick of the event loop to give the browser some
      // time in between to paint (e.g. a spinner)
      setTimeout(() => {
        void (async () => {
          // Uncomment line below for command performance measuring
          // const start = performance.now();

          // Clone the model content. `content` is non-null whenever a command runs.
          const model = clone(this.modelVersion.content!);
          // Attach the action recorder
          const recorder = recordActions(model);

          // Run the command
          const checks = await command(model);

          // Execute the checks and generate the report. First filter out obsolete checks (i.e. checks for
          // elements that have been deleted after the check was added to the list of checks).
          // `check()` lives only on the concrete subclasses (see RunnableCheck); the
          // commands layer guarantees the bag holds runnable checks.
          const reports = (checks as unknown as RunnableCheck[])
            .filter((c) => !c.obsolete)
            .map((c) => c.check());
          const report = Report.mergeReports(reports);
          if (report.successful) {
            // Replay the actions on the original model
            recorder.replay(this.modelVersion.content!);
          }
          // Uncomment 2 lines below for command performance measuring
          // const end = performance.now();
          // console.log(`command took ${end - start} ms`);
          resolve(report);
        })().catch(reject);
      }, 50);
    });
    return report;
  }
}
